import Long from 'long';
import { fetchFileBuffer } from '../../fetcher';
import { PPCPointCloudGeometry } from '../../gemoetries/PPCPointCloudGeometry';
import { PPCPointCloudGeometryNode } from '../../gemoetries/PPCPointCloudGeometry/PPCPointCloudGeometryNode';
import { PPCHReader, SEGMENT } from '../../ppcreader/PPCHReader';
import unifiedWorkerPool from '../../utils/workers/UnifiedWorkerPool';

export class PPCLoader {
  static async load(url: string, name: string) {
    const regex = /^\/([^/]+)\/([^/]+)\.ppch(\?.+)?$/;
    const pcshUrl = url.replace(regex, (match, id1, id2name, token) => {
      return `${id1}/${id2name}/${id2name}.pcsh${token || ''}`;
    });
    const { ppchReader } = await this.parsePPCH(url);
    // The segment list exists only for a segmented cloud, and the header says so (the converter writes
    // both together). Not asking for it otherwise keeps a cloud without one (e.g. a flat scan) from
    // logging a 404 on every load.
    const segmented = ppchReader.mFieldInfo?.mFields?.[SEGMENT] === true;
    const ppcshResult = segmented ? await this.parsePPCSH(pcshUrl) : undefined;
    const res = ppcshResult as { data: { list: unknown[] } } | undefined;
    const segmentList = res?.data.list;

    // Create the root node here
    const ppcGeometry = new PPCPointCloudGeometry({ name, ppchReader });
    const root = new PPCPointCloudGeometryNode({
      boundingBox: ppcGeometry.boundingBox,
      chunkIndex: 1,
      index: NaN,
      level: 0,
      name: 'r',
      ppcGeometry,
      voxelId: new Long(0, 0, true),
    });

    ppcGeometry.root = root;
    ppcGeometry.root.load();

    // TODO:: remove, just for demo purposes 26/08
    setTimeout(() => {
      document.getElementById('globe')?.remove();
    }, 2000);

    return { ppcGeometry, segmentList };
  }

  static async parsePPCH(url: string) {
    const ppchBuffer = await fetchFileBuffer(url);
    if (!ppchBuffer) throw new Error(`Failed to fetch PPCH: ${url}`);
    const ppchReader = new PPCHReader();
    ppchReader.loadBuffer(ppchBuffer);
    const fileSignature = ppchBuffer.slice(0, 256);

    return { ppchReader, fileSignature };
  }

  // Parse segment header. Optional: a cloud that was not segmented (e.g. a flat one) has none, and
  // must load without waiting on retries.
  static async parsePPCSH(url: string) {
    const fileBuffer = await fetchFileBuffer(url, { optional: true });
    if (!fileBuffer) return;
    // Use the public copy directly to bypass Vite's bundling.
    // The worker uses importScripts which is incompatible with module workers.
    const workerPath = '/potree/wasm/PPCWorker.js';
    const entry = await unifiedWorkerPool.getWorker(workerPath);
    let failed = false;
    try {
      const result = await new Promise((resolve) => {
        entry.worker.onmessage = (event: MessageEvent) => {
          resolve(event);
        };
        // A failed worker only costs the segment list, the cloud itself must still load.
        entry.worker.onerror = (error) => {
          console.warn('PPCLoader: segment header worker failed', error.message);
          failed = true;
          resolve(undefined);
        };

        const message = {
          buffer: fileBuffer,
          fileUrl: url,
          fileExtension: 'pcsh',
        };
        entry.worker.postMessage(message, [message.buffer]);
      });
      return result;
    } finally {
      if (failed) unifiedWorkerPool.discardWorker(entry);
      else unifiedWorkerPool.returnWorker(workerPath, entry);
    }
  }
}
